import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'redux/hooks';
import { authSelectors } from 'redux/modules/Authorization';
import { captureEvent } from 'utils/analytics/captureEvent';
import {
  getTotalVisits,
  setTotalVisits,
  getTotalTimeOnSiteSeconds,
  addTotalTimeOnSiteSeconds,
} from 'utils/analytics/visitStorage';
import { AnalyticsEvents } from 'enums';

const VISIT_TIMEOUT_MS = 30 * 60 * 1000;
const SESSION_VISIT_START = 'analytics_visit_start';
const SESSION_LAST_PAGE_VIEW = 'analytics_last_page_view';
const SESSION_PAGES_IN_VISIT = 'analytics_pages_in_visit';
const SESSION_USER_ID = 'analytics_user_id';

export const usePageViewTracking = (): void => {
  const { pathname } = useLocation();
  const user = useAppSelector(authSelectors.getUser);
  const userId = user?.id ?? null;

  useEffect(() => {
    try {
      sessionStorage.setItem(SESSION_USER_ID, String(userId ?? 'anonymous'));
    } catch {
      // intentionally empty
    }
  }, [userId]);

  useEffect(() => {
    const onLeave = () => {
      try {
        const lastView = parseInt(
          sessionStorage.getItem(SESSION_LAST_PAGE_VIEW) ?? '0',
          10,
        );
        if (lastView <= 0) return;
        const uidRaw = sessionStorage.getItem(SESSION_USER_ID);
        const parsed =
          uidRaw === 'anonymous' || !uidRaw ? null : parseInt(uidRaw, 10);
        const userIdForLeave = Number.isNaN(parsed ?? NaN) ? null : parsed;
        const seconds = Math.round((Date.now() - lastView) / 1000);
        if (seconds > 0) {
          addTotalTimeOnSiteSeconds(userIdForLeave, seconds);
        }
      } catch {
        // intentionally empty
      }
    };
    window.addEventListener('beforeunload', onLeave);
    window.addEventListener('pagehide', onLeave);
    return () => {
      window.removeEventListener('beforeunload', onLeave);
      window.removeEventListener('pagehide', onLeave);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.dataLayer) return;

    const now = Date.now();
    let visitStart: number;
    let lastView: number;
    let pagesInVisit: number;

    try {
      visitStart = parseInt(
        sessionStorage.getItem(SESSION_VISIT_START) ?? '0',
        10,
      );
      lastView = parseInt(
        sessionStorage.getItem(SESSION_LAST_PAGE_VIEW) ?? '0',
        10,
      );
      pagesInVisit = parseInt(
        sessionStorage.getItem(SESSION_PAGES_IN_VISIT) ?? '0',
        10,
      );
    } catch {
      visitStart = 0;
      lastView = 0;
      pagesInVisit = 0;
    }

    const isNewVisit = !visitStart || now - lastView > VISIT_TIMEOUT_MS;

    if (isNewVisit) {
      const total = getTotalVisits(userId);
      setTotalVisits(userId, total + 1);
      visitStart = now;
      pagesInVisit = 0;
      try {
        sessionStorage.setItem(SESSION_VISIT_START, String(visitStart));
      } catch {
        // intentionally empty
      }
    }

    const isRootPath = pathname === '/';
    if (!isRootPath) {
      pagesInVisit += 1;
    }
    const totalVisits = Math.max(1, getTotalVisits(userId));

    const timeOnPageSeconds =
      lastView > 0 ? Math.round((now - lastView) / 1000) : 0;
    if (timeOnPageSeconds > 0) {
      addTotalTimeOnSiteSeconds(userId, timeOnPageSeconds);
    }
    const sessionDurationSeconds = Math.round((now - visitStart) / 1000);
    const totalTimeOnSiteSeconds = Math.round(
      getTotalTimeOnSiteSeconds(userId),
    );
    if (user?.id) {
      captureEvent(AnalyticsEvents.PageView, {
        page_path: pathname,
        user_id: String(user?.id),
        total_visits: totalVisits,
        pages_in_visit: pagesInVisit,
        time_on_page_seconds: timeOnPageSeconds,
        session_duration_seconds: sessionDurationSeconds,
        total_time_on_site_seconds: totalTimeOnSiteSeconds,
        event_time: new Date().toISOString(),
      });
    }

    try {
      sessionStorage.setItem(SESSION_LAST_PAGE_VIEW, String(now));
      if (!isRootPath) {
        sessionStorage.setItem(SESSION_PAGES_IN_VISIT, String(pagesInVisit));
      }
    } catch {
      // intentionally empty
    }
  }, [pathname, userId]);
};
